// Email Service
// Secure Gmail OAuth token management and database operations for Supabase

import CryptoJS from 'crypto-js';
import GmailOAuthService from './gmail/GmailOAuthService';
import { supabase, handleSupabaseError } from '@/lib/supabase';
import {
  getEmailConnections,
  getEmailConnectionByAddress,
  addEmailConnection,
  updateEmailConnection as updateEmailConnectionMetadata,
  removeEmailConnection,
  updateLastSyncDate,
  toggleSyncEnabled,
  getEnabledEmailConnections,
  hasEmailConnections
} from '@/lib/database/email-connections-helpers';
// TEMPORARILY COMMENTED: Google integration - will restore later
// import { updateProfilePictureFromGoogle } from './profileService';

/**
 * Email Service Class
 * Handles secure storage and management of Gmail OAuth tokens
 * NOTE: This service handles OAuth tokens separately from the JSONB email_connections metadata
 */
class EmailService {
  constructor() {
    this.encryptionKey = import.meta.env.VITE_TOKEN_ENCRYPTION_KEY;
    
    if (!this.encryptionKey) {
      console.warn('EmailService: Token encryption key not found. Tokens will not be encrypted.');
    }
  }

  /**
   * Encrypt a token for secure storage
   * @param {string} token - Token to encrypt
   * @returns {string} Encrypted token
   */
  encryptToken(token) {
    if (!this.encryptionKey) {
      console.warn('EmailService: No encryption key available. Storing token in plain text.');
      return token;
    }

    try {
      const encrypted = CryptoJS.AES.encrypt(token, this.encryptionKey).toString();
      return encrypted;
    } catch (error) {
      console.error('EmailService: Error encrypting token:', error);
      throw new Error('Failed to encrypt token');
    }
  }

  /**
   * Decrypt a token from storage
   * @param {string} encryptedToken - Encrypted token
   * @returns {string} Decrypted token
   */
  decryptToken(encryptedToken) {
    if (!this.encryptionKey) {
      // Try to return as-is if no encryption key (for backward compatibility)
      return encryptedToken;
    }

    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedToken, this.encryptionKey);
      const token = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!token) {
        throw new Error('Invalid encrypted token');
      }
      
      return token;
    } catch (error) {
      console.error('EmailService: Error decrypting token:', error);
      throw new Error('Failed to decrypt token');
    }
  }

  /**
   * Save email connection to database
   * @param {string} userId - User ID
   * @param {Object} oauthData - OAuth data from Gmail component
   * @returns {Promise<Object>} Saved email connection
   */
  async saveEmailConnection(userId, oauthData) {
    try {
      console.log('EmailService: Saving email connection for user:', userId);

      // Validate required fields
      if (!userId || !oauthData?.access_token || !oauthData?.email) {
        throw new Error('Missing required fields: userId, access_token, or email');
      }

      // Encrypt tokens
      const accessTokenEncrypted = this.encryptToken(oauthData.access_token);
      const refreshTokenEncrypted = oauthData.refresh_token 
        ? this.encryptToken(oauthData.refresh_token)
        : null;

      // Check if connection already exists
      const existingConnection = await this.getEmailConnection(userId);
      
      // Calculate token expiry (1 hour from now)
      const tokenExpiresAt = new Date(Date.now() + 3600000).toISOString(); // 1 hour = 3600000ms
      
      const connectionData = {
        user_id: userId,
        email_address: oauthData.email,
        access_token_encrypted: accessTokenEncrypted,
        refresh_token_encrypted: refreshTokenEncrypted,
        token_expires_at: tokenExpiresAt, // Add expires_at (1 hour from now)
        provider: 'gmail',
        sync_enabled: true,
        updated_at: new Date().toISOString()
      };

      let result;
      if (existingConnection) {
        // Update existing connection
        console.log('EmailService: Updating existing email connection');
        const { data, error } = await supabase
          .from('user_email_connections')
          .update(connectionData)
          .eq('user_id', userId)
          .select()
          .single();

        // If error is due to missing column, retry without token_expires_at
        if (error && (error.code === 'PGRST204' || error.message?.includes('token_expires_at'))) {
          console.warn('EmailService: token_expires_at column not found, retrying without it. Please run migration to add column.');
          const { token_expires_at, ...connectionDataWithoutExpiry } = connectionData;
          const { data: retryData, error: retryError } = await supabase
            .from('user_email_connections')
            .update(connectionDataWithoutExpiry)
            .eq('user_id', userId)
            .select()
            .single();
          
          if (retryError) throw retryError;
          result = retryData;
        } else if (error) {
          throw error;
        } else {
          result = data;
        }
      } else {
        // Create new connection
        console.log('EmailService: Creating new email connection');
        const { data, error } = await supabase
          .from('user_email_connections')
          .insert({
            ...connectionData,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        // If error is due to missing column, retry without token_expires_at
        if (error && (error.code === 'PGRST204' || error.message?.includes('token_expires_at'))) {
          console.warn('EmailService: token_expires_at column not found, retrying without it. Please run migration to add column.');
          const { token_expires_at, ...connectionDataWithoutExpiry } = connectionData;
          const { data: retryData, error: retryError } = await supabase
            .from('user_email_connections')
            .insert({
              ...connectionDataWithoutExpiry,
              created_at: new Date().toISOString()
            })
            .select()
            .single();
          
          if (retryError) throw retryError;
          result = retryData;
        } else if (error) {
          throw error;
        } else {
          result = data;
        }
      }

      console.log('EmailService: Email connection saved successfully');
      
      // TEMPORARILY COMMENTED: Google integration - will restore later
      // Update profile picture from Google (non-blocking)
      /* try {
        console.log('EmailService: Syncing profile picture from Google...');
        const profileResult = await updateProfilePictureFromGoogle(
          userId,
          oauthData.access_token
        );
        
        if (profileResult.success) {
          console.log('✅ Profile picture synced from Google');
        } else {
          console.warn('⚠️ Profile picture sync failed:', profileResult.error);
        }
      } catch (profileError) {
        // Don't throw - profile picture sync failure shouldn't block email connection
        console.warn('⚠️ Error syncing profile picture (non-critical):', profileError);
      } */
      
      return result;

    } catch (error) {
      console.error('EmailService: Error saving email connection:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get email connection for user
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Email connection or null
   */
  async getEmailConnection(userId) {
    try {
      console.log('EmailService: Getting email connection for user:', userId);

      const { data, error } = await supabase
        .from('user_email_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', 'gmail');

      if (error) {
        console.error('EmailService: Error getting email connection:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('EmailService: No email connection found for user');
        return null;
      }

      console.log('EmailService: Found', data.length, 'email connection(s) for user');
      console.log('EmailService: Email connections:', data.map(conn => ({
        id: conn.id,
        email_address: conn.email_address,
        created_at: conn.created_at,
        updated_at: conn.updated_at
      })));

      // If multiple connections exist, clean up duplicates and return the most recent one
      if (data.length > 1) {
        console.log('EmailService: Multiple email connections found, cleaning up duplicates...');
        await this.cleanupDuplicateConnections(userId, data);
        
        // Re-fetch after cleanup
        const { data: cleanedData, error: cleanedError } = await supabase
          .from('user_email_connections')
          .select('*')
          .eq('user_id', userId)
          .eq('provider', 'gmail');
          
        if (cleanedError) throw cleanedError;
        
        if (cleanedData && cleanedData.length > 0) {
          const selectedConnection = cleanedData[0];
          console.log('EmailService: Selected connection after cleanup:', {
            id: selectedConnection.id,
            email_address: selectedConnection.email_address,
            updated_at: selectedConnection.updated_at
          });
          return selectedConnection;
        }
      }

      const selectedConnection = data[0];
      console.log('EmailService: Selected connection:', {
        id: selectedConnection.id,
        email_address: selectedConnection.email_address,
        updated_at: selectedConnection.updated_at
      });

      return selectedConnection;

    } catch (error) {
      console.error('EmailService: Error getting email connection:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get decrypted email connection for user
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Decrypted email connection or null
   */
  async getDecryptedEmailConnection(userId) {
    try {
      const connection = await this.getEmailConnection(userId);
      
      if (!connection) {
        return null;
      }

      // Decrypt tokens
      const decryptedConnection = {
        ...connection,
        access_token: this.decryptToken(connection.access_token_encrypted),
        refresh_token: connection.refresh_token_encrypted 
          ? this.decryptToken(connection.refresh_token_encrypted)
          : null
      };

      // Remove encrypted fields
      delete decryptedConnection.access_token_encrypted;
      delete decryptedConnection.refresh_token_encrypted;

      return decryptedConnection;

    } catch (error) {
      console.error('EmailService: Error getting decrypted email connection:', error);
      throw error;
    }
  }

  /**
   * Update email connection
   * @param {string} userId - User ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated email connection
   */
  async updateEmailConnection(userId, updates) {
    try {
      console.log('EmailService: Updating email connection for user:', userId);

      // Encrypt tokens if they're being updated
      const updateData = { ...updates };
      
      if (updates.access_token) {
        updateData.access_token_encrypted = this.encryptToken(updates.access_token);
        delete updateData.access_token;
      }
      
      if (updates.refresh_token) {
        updateData.refresh_token_encrypted = this.encryptToken(updates.refresh_token);
        delete updateData.refresh_token;
      }

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('user_email_connections')
        .update(updateData)
        .eq('user_id', userId)
        .eq('provider', 'gmail')
        .select()
        .single();

      if (error) throw error;

      console.log('EmailService: Email connection updated successfully');
      return data;

    } catch (error) {
      console.error('EmailService: Error updating email connection:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Toggle email sync for user
   * @param {string} userId - User ID
   * @param {boolean} enabled - Whether to enable sync
   * @returns {Promise<boolean>} Success status
   */
  async toggleEmailSync(userId, enabled) {
    try {
      console.log('EmailService: Toggling email sync for user:', userId, 'enabled:', enabled);

      const { error } = await supabase
        .from('user_email_connections')
        .update({ 
          sync_enabled: enabled,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('provider', 'gmail');

      if (error) throw error;

      console.log('EmailService: Email sync toggled successfully');
      return true;

    } catch (error) {
      console.error('EmailService: Error toggling email sync:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Delete email connection for user
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteEmailConnection(userId) {
    try {
      console.log('EmailService: Deleting email connection for user:', userId);

      const { error } = await supabase
        .from('user_email_connections')
        .delete()
        .eq('user_id', userId)
        .eq('provider', 'gmail');

      if (error) throw error;

      console.log('EmailService: Email connection deleted successfully');
      return true;

    } catch (error) {
      console.error('EmailService: Error deleting email connection:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Refresh Gmail access token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshGmailToken(refreshToken) {
    try {
      console.log('EmailService: Refreshing Gmail token');

      const newTokens = await GmailOAuthService.refreshAccessToken(refreshToken);
      
      console.log('EmailService: Gmail token refreshed successfully');
      return newTokens;

    } catch (error) {
      console.error('EmailService: Error refreshing Gmail token:', error);
      throw new Error(`Failed to refresh Gmail token: ${error.message}`);
    }
  }

  /**
   * Get email connection statistics
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Connection statistics
   */
  async getEmailConnectionStats(userId) {
    try {
      console.log('EmailService: Getting email connection stats for user:', userId);

      // Use helper function to get email connections metadata
      const connections = await getEmailConnections(userId);

      const stats = {
        total_connections: connections.length,
        active_sync_count: connections.filter(conn => conn.sync_enabled).length,
        last_sync_at: connections.length > 0 ? connections[0].updated_at : null,
        provider_breakdown: {
          gmail: connections.filter(conn => conn.provider === 'gmail').length
        }
      };

      console.log('EmailService: Email connection stats retrieved successfully');
      return stats;

    } catch (error) {
      console.error('EmailService: Error getting email connection stats:', error);
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get email sync status for user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Sync status
   */
  async getEmailSyncStatus(userId) {
    try {
      const connection = await this.getEmailConnection(userId);
      
      if (!connection) {
        return {
          is_connected: false,
          is_sync_enabled: false,
          error: 'No email connection found'
        };
      }

      return {
        is_connected: true,
        is_sync_enabled: connection.sync_enabled,
        last_sync_at: connection.updated_at,
        connection_id: connection.id,
        email_address: connection.email_address,
        provider: connection.provider
      };

    } catch (error) {
      console.error('EmailService: Error getting email sync status:', error);
      return {
        is_connected: false,
        is_sync_enabled: false,
        error: error.message
      };
    }
  }

  /**
   * Clean up duplicate email connections for a user
   * @param {string} userId - User ID
   * @param {Array} connections - Array of connection records
   * @returns {Promise<void>}
   */
  async cleanupDuplicateConnections(userId, connections) {
    try {
      console.log('EmailService: Cleaning up duplicate connections for user:', userId);
      
      // Sort by updated_at descending to keep the most recent one
      const sortedConnections = connections.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      const keepConnection = sortedConnections[0];
      const deleteConnections = sortedConnections.slice(1);
      
      console.log('EmailService: Keeping connection:', keepConnection.id, 'Deleting:', deleteConnections.map(c => c.id));
      
      // Delete duplicate connections
      if (deleteConnections.length > 0) {
        const idsToDelete = deleteConnections.map(conn => conn.id);
        const { error } = await supabase
          .from('user_email_connections')
          .delete()
          .in('id', idsToDelete);
          
        if (error) throw error;
        
        console.log('EmailService: Successfully deleted', deleteConnections.length, 'duplicate connections');
      }
      
    } catch (error) {
      console.error('EmailService: Error cleaning up duplicate connections:', error);
      throw error;
    }
  }

  /**
   * Validate email connection
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} Whether connection is valid
   */
  async validateEmailConnection(userId) {
    try {
      const connection = await this.getDecryptedEmailConnection(userId);
      
      if (!connection) {
        return false;
      }

      // Check if tokens are expired (basic check)
      const now = Date.now();
      const tokenAge = now - new Date(connection.updated_at).getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      if (tokenAge > maxAge) {
        console.log('EmailService: Email connection tokens are old, may need refresh');
        return false;
      }

      return true;

    } catch (error) {
      console.error('EmailService: Error validating email connection:', error);
      return false;
    }
  }
}

// Create and export singleton instance
const emailService = new EmailService();

// Export individual functions for convenience
export const encryptToken = (token) => emailService.encryptToken(token);
export const decryptToken = (encryptedToken) => emailService.decryptToken(encryptedToken);
export const saveEmailConnection = (userId, oauthData) => emailService.saveEmailConnection(userId, oauthData);
export const getEmailConnection = (userId) => emailService.getEmailConnection(userId);
export const getDecryptedEmailConnection = (userId) => emailService.getDecryptedEmailConnection(userId);
export const updateEmailConnection = (userId, updates) => emailService.updateEmailConnection(userId, updates);
export const toggleEmailSync = (userId, enabled) => emailService.toggleEmailSync(userId, enabled);
export const deleteEmailConnection = (userId) => emailService.deleteEmailConnection(userId);
export const refreshGmailToken = (refreshToken) => emailService.refreshGmailToken(refreshToken);
export const getEmailConnectionStats = (userId) => emailService.getEmailConnectionStats(userId);
export const getEmailSyncStatus = (userId) => emailService.getEmailSyncStatus(userId);
export const validateEmailConnection = (userId) => emailService.validateEmailConnection(userId);

export default emailService;
